import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WEEKDAYS, type CloseTime, type DrawConfig, type GameConfig, type PlannedDraw, type ProductConfig, type ProductVersionView, type ResultPatternView } from '@lp/contracts';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { errorText, fieldProblems, get, post, put } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { DeleteDraftButton } from '@/components/delete-draft';
import { ConfigGaps, PlannedDraws, ProductConfigView } from '@/components/product-config';
import { Badge, Button, Card, Dialog, ErrorBox, Field, Input, Loading, PageHeader, Select } from '@/components/ui';

const DAY_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ZONES = ['Asia/Phnom_Penh', 'Asia/Bangkok', 'Asia/Ho_Chi_Minh'];
const codesOf = (s: string) => s.split(/[\s,]+/).filter(Boolean);
const everyDay = (at: CloseTime): CloseTime => (typeof at === 'string' ? at : at.mon);
const perWeekday = (at: CloseTime): CloseTime => (typeof at === 'string' ? (Object.fromEntries(WEEKDAYS.map((d) => [d, at])) as Exclude<CloseTime, string>) : at);

/** the draft editor: change a product without code — then preview its draws and publish it (a published version never changes) */
export function ProductEditPage() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const code = (useParams().code ?? '').toUpperCase();
  const version = Number(useParams().version);
  const v = useQuery({ queryKey: ['product', code, 'version', version], queryFn: () => get<ProductVersionView>(`/platform/products/${code}/versions/${version}`) });
  const [config, setConfig] = useState<ProductConfig | null>(null);
  const patterns = useQuery({ queryKey: ['patterns'], queryFn: () => get<ResultPatternView[]>('/platform/patterns') });
  const [note, setNote] = useState('');
  const [dirty, setDirty] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirm, setConfirm] = useState('');
  useEffect(() => {
    if (v.data && !config) {
      setConfig(structuredClone(v.data.config));
      setNote(v.data.note ?? '');
    }
  }, [v.data, config]);

  const save = useMutation({
    mutationFn: () => put<ProductVersionView>(`/platform/products/${code}/versions/${version}`, { config, note: note || undefined }),
    onSuccess: () => {
      setDirty(false);
      void qc.invalidateQueries({ queryKey: ['product', code] });
    },
  });
  const preview = useQuery({ queryKey: ['product', code, 'preview', version, save.data], queryFn: () => get<{ timezone: string; draws: PlannedDraw[] }>(`/platform/products/${code}/schedule?days=7&version=${version}`), enabled: !!save.data && !dirty });
  const publish = useMutation({
    mutationFn: () => post(`/platform/products/${code}/versions/${version}/publish`, { confirmCode: confirm }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['product', code] });
      void qc.invalidateQueries({ queryKey: ['products'] });
      nav(`/products/${code}`);
    },
  });

  if (v.isLoading || (v.data && !config)) return <Loading />;
  if (v.error || !v.data || !config) return <ErrorBox text={errorText(v.error)} />;
  if (v.data.status !== 'draft')
    return (
      <>
        <ErrorBox text={t('This version is published and never changes. Make a new version to change the product.')} />
        <div className="mt-4">
          <ProductConfigView config={v.data.config} />
        </div>
      </>
    );

  const change = (f: (c: ProductConfig) => void) => {
    const next = structuredClone(config);
    f(next);
    setConfig(next);
    setDirty(true);
  };
  const game = (gi: number, f: (g: GameConfig) => void) => change((c) => f(c.games[gi]!));
  const draw = (gi: number, di: number, f: (d: DrawConfig) => void) => game(gi, (g) => f(g.draws[di]!));
  const problems = fieldProblems(save.error);

  return (
    <>
      <Link to={`/products/${code}`} className="mb-2 inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-800">
        <ArrowLeft size={12} /> {code}
      </Link>
      <PageHeader
        title={`${t('Edit draft')} — ${code} v${version}`}
        subtitle={t('Nothing here is in force until it is published. A client moves to the new version only from its own page.')}
        actions={
          <>
            <DeleteDraftButton
              path={`/platform/products/${code}/versions/${version}`}
              label={`${code} v${version}`}
              onDeleted={() => {
                void qc.invalidateQueries({ queryKey: ['product', code] });
                void qc.invalidateQueries({ queryKey: ['products'] });
                nav(`/products/${code}`);
              }}
            />
            <Button variant="secondary" onClick={() => save.mutate()} disabled={save.isPending || !dirty}>
              {dirty ? t('Save draft') : t('Saved')}
            </Button>
            <Button onClick={() => setPublishing(true)} disabled={dirty}>
              {t('Publish')}
            </Button>
          </>
        }
      />
      {save.error && <ErrorBox text={[errorText(save.error), ...problems].join(' — ')} />}

      <Card title={t('Product')} className="mb-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={t('Time zone')}>
            <Select value={config.timezone} onChange={(e) => change((c) => (c.timezone = e.target.value))} className="w-full">
              {[...new Set([config.timezone, ...ZONES])].map((z) => (
                <option key={z}>{z}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('Posts')}>
            <Input
              key={config.posts.map((p) => p.code).join(' ')}
              defaultValue={config.posts.map((p) => p.code).join(' ')}
              onBlur={(e) =>
                change((c) => {
                  const kinds = new Map(c.posts.map((p) => [p.code, p.kind]));
                  c.posts = codesOf(e.target.value).map((code) => ({ code, kind: kinds.get(code) ?? 'single' }));
                })
              }
              className="font-mono"
            />
          </Field>
          <Field label={t('Note')}>
            <Input
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                setDirty(true);
              }}
              maxLength={300}
            />
          </Field>
        </div>
      </Card>

      {config.games.map((g, gi) => (
        <Card key={g.code} className="mb-4" title={<span>{g.nameEn} <span className="font-mono text-xs text-ink-500">{g.code}</span> {g.rule ? <Badge tone="green">{g.rule}</Badge> : <Badge>{t('Every post typed by hand')}</Badge>}</span>}>
          <div className="mb-4 grid gap-3 sm:grid-cols-4">
            <Field label={t('Name')}>
              <Input value={g.nameEn} onChange={(e) => game(gi, (x) => (x.nameEn = e.target.value))} />
            </Field>
            <Field label={t('Name (Khmer)')}>
              <Input value={g.nameKm} onChange={(e) => game(gi, (x) => (x.nameKm = e.target.value))} />
            </Field>
            <Field label={t('Result pattern')}>
              <Select value={g.rule ?? ''} onChange={(e) => game(gi, (x) => (x.rule = e.target.value || null))} className="w-full" aria-label={`${t('Result pattern')} ${g.code}`}>
                <option value="">{t('Every post typed by hand')}</option>
                {[...new Set([...(g.rule ? [g.rule] : []), ...(patterns.data ?? []).map((p) => p.code)])].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('Draw days')}>
              <Select value={g.schedule.kind} onChange={(e) => game(gi, (x) => (x.schedule = e.target.value === 'monthly' ? { kind: 'monthly', dates: [1, 16] } : { kind: 'weekly', days: [0, 1, 2, 3, 4, 5, 6] }))} className="w-full">
                <option value="weekly">{t('Days of the week')}</option>
                <option value="monthly">{t('Dates of each month')}</option>
              </Select>
            </Field>
            <Field label={t('Default close (minutes before the draw)')}>
              <Input type="number" min={0} max={240} value={g.defaultCloseMinutes} onChange={(e) => game(gi, (x) => (x.defaultCloseMinutes = Number(e.target.value)))} />
            </Field>
          </div>
          <div className="mb-4 grid gap-3 sm:grid-cols-5" data-testid={`betting-${g.code}`}>
            <Field label={t('Pays 2D ×')}>
              <Input type="number" min={1} value={g.betting.payout['2']} onChange={(ev) => game(gi, (x) => (x.betting.payout['2'] = Number(ev.target.value)))} />
            </Field>
            <Field label={t('Pays 3D ×')}>
              <Input type="number" min={1} value={g.betting.payout['3']} onChange={(ev) => game(gi, (x) => (x.betting.payout['3'] = Number(ev.target.value)))} />
            </Field>
            <Field label={t('Least per number')}>
              <Input type="number" min={1} value={g.betting.minAmount} onChange={(ev) => game(gi, (x) => (x.betting.minAmount = Number(ev.target.value)))} />
            </Field>
            <Field label={t('Step')}>
              <Input type="number" min={1} value={g.betting.amountStep} onChange={(ev) => game(gi, (x) => (x.betting.amountStep = Number(ev.target.value)))} />
            </Field>
            <Field label={t('Most per number')}>
              <Input type="number" min={1} value={g.betting.maxAmountPerNumber} onChange={(ev) => game(gi, (x) => (x.betting.maxAmountPerNumber = Number(ev.target.value)))} />
            </Field>
          </div>
          <div className="mb-4">
            {g.schedule.kind === 'weekly' ? (
              <div className="flex flex-wrap gap-3 text-sm">
                {DAY_LABEL.map((d, i) => (
                  <label key={d} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={g.schedule.kind === 'weekly' && g.schedule.days.includes(i)}
                      onChange={(e) =>
                        game(gi, (x) => {
                          if (x.schedule.kind !== 'weekly') return;
                          x.schedule.days = e.target.checked ? [...x.schedule.days, i].sort() : x.schedule.days.filter((y) => y !== i);
                        })
                      }
                    />
                    {t(d)}
                  </label>
                ))}
              </div>
            ) : (
              <Field label={t('Dates (e.g. 1 16)')}>
                <Input
                  key={g.schedule.dates.join(' ')}
                  defaultValue={g.schedule.dates.join(' ')}
                  onBlur={(e) =>
                    game(gi, (x) => {
                      if (x.schedule.kind === 'monthly') x.schedule.dates = codesOf(e.target.value).map(Number).filter((n) => n >= 1 && n <= 31);
                    })
                  }
                  className="max-w-xs"
                />
              </Field>
            )}
          </div>

          <div className="space-y-3">
            {g.draws.map((d, di) => (
              <div key={di} className="rounded-md border border-ink-200 p-3">
                <div className="mb-2 flex flex-wrap items-end gap-3">
                  <Field label={t('Draw time')}>
                    <Input type="time" value={d.time} onChange={(e) => draw(gi, di, (x) => (x.time = e.target.value))} className="w-32" />
                  </Field>
                  <Field label={t('Posts (empty = all)')}>
                    <Input key={`${di}:${(d.posts ?? []).join(' ')}`} defaultValue={(d.posts ?? []).join(' ')} onBlur={(e) => draw(gi, di, (x) => (x.posts = codesOf(e.target.value).length ? codesOf(e.target.value) : undefined))} className="w-72 font-mono" />
                  </Field>
                  <DrawResultField d={d} patterns={patterns.data ?? []} onChange={(f) => draw(gi, di, f)} />
                  <Button variant="ghost" className="ml-auto" onClick={() => game(gi, (x) => x.draws.splice(di, 1))} disabled={g.draws.length === 1} aria-label={t('Remove draw')} title={t('Remove draw')}>
                    <Trash2 size={14} />
                  </Button>
                </div>
                <div className="space-y-2">
                  {d.closes.map((c, ci) => (
                    <div key={ci} className="flex flex-wrap items-center gap-2 text-xs">
                      <Input key={`${ci}:${c.posts.join(' ')}`} defaultValue={c.posts.join(' ')} onBlur={(e) => draw(gi, di, (x) => (x.closes[ci]!.posts = codesOf(e.target.value)))} className="h-8 w-40 font-mono text-xs" aria-label={t('Posts')} />
                      <Select value={typeof c.at === 'string' ? 'same' : 'week'} onChange={(e) => draw(gi, di, (x) => (x.closes[ci]!.at = e.target.value === 'same' ? everyDay(x.closes[ci]!.at) : perWeekday(x.closes[ci]!.at)))} className="h-8 text-xs">
                        <option value="same">{t('Same every day')}</option>
                        <option value="week">{t('Per weekday')}</option>
                      </Select>
                      {typeof c.at === 'string' ? (
                        <Input type="time" value={c.at} onChange={(e) => draw(gi, di, (x) => (x.closes[ci]!.at = e.target.value))} className="h-8 w-28 text-xs" />
                      ) : (
                        [...WEEKDAYS.slice(1), WEEKDAYS[0]].map((wd) => (
                          <label key={wd} className="flex flex-col items-center">
                            <span className="text-[10px] text-ink-500">{t(DAY_LABEL[WEEKDAYS.indexOf(wd)]!)}</span>
                            <Input
                              type="time"
                              value={(c.at as Record<string, string>)[wd]}
                              onChange={(e) => draw(gi, di, (x) => ((x.closes[ci]!.at as Record<string, string>)[wd] = e.target.value))}
                              className="h-8 w-24 px-1 text-xs"
                            />
                          </label>
                        ))
                      )}
                      <button className="text-ink-400 hover:text-red-600" onClick={() => draw(gi, di, (x) => x.closes.splice(ci, 1))} aria-label={t('Remove')} title={t('Remove')}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                  <Button variant="ghost" icon={<Plus size={13} />} onClick={() => draw(gi, di, (x) => x.closes.push({ posts: [], at: x.time }))}>
                    {t('Close time for posts')}
                  </Button>
                </div>
              </div>
            ))}
            <Button variant="secondary" icon={<Plus size={14} />} onClick={() => game(gi, (x) => x.draws.push({ time: '12:00', closes: [] }))}>
              {t('Add a draw')}
            </Button>
          </div>
        </Card>
      ))}

      <GapsEditor config={config} change={change} />

      {save.data && !dirty && (
        <Card title={t('Next 7 days with this draft')} className="mb-4">
          {preview.isLoading ? <Loading /> : preview.data ? <PlannedDraws draws={preview.data.draws} timezone={preview.data.timezone} /> : <ErrorBox text={errorText(preview.error)} />}
        </Card>
      )}

      {publishing && (
        <Dialog
          title={`${t('Publish')} ${code} v${version}`}
          onClose={() => setPublishing(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setPublishing(false)}>
                {t('Cancel')}
              </Button>
              <Button onClick={() => publish.mutate()} disabled={publish.isPending || confirm.trim().toUpperCase() !== code}>
                {t('Publish')}
              </Button>
            </>
          }
        >
          <p>{t('A published version never changes. Clients stay on their version until you move them.')}</p>
          <Field label={`${t('Type the product code to confirm')}: ${code}`}>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} autoFocus />
          </Field>
          {publish.error && <ErrorBox text={[errorText(publish.error), ...fieldProblems(publish.error)].join(' — ')} />}
        </Dialog>
      )}
    </>
  );
}

/**
 * where a draw's result comes from: its game's pattern (the usual), a pattern of its own, every post by hand, or the same result as another
 * draw (owner 2026-09-22: TN Old 10:25 = Thinh Nam 10:30) — a draw that follows another uses that draw's pattern and never its own
 */
function DrawResultField({ d, patterns, onChange }: { d: DrawConfig; patterns: ResultPatternView[]; onChange: (f: (x: DrawConfig) => void) => void }) {
  const t = useT();
  const mode = d.resultAs ? 'as' : d.rule === undefined ? 'game' : d.rule === null ? 'hand' : `rule:${d.rule}`;
  const set = (m: string) =>
    onChange((x) => {
      delete x.resultAs;
      delete x.rule;
      if (m === 'hand') x.rule = null;
      else if (m.startsWith('rule:')) x.rule = m.slice(5);
      else if (m === 'as') x.resultAs = { product: '', game: '', time: x.time };
    });
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Field label={t('Result')}>
        <Select value={mode} onChange={(e) => set(e.target.value)} className="w-56" aria-label={`${t('Result')} ${d.time}`}>
          <option value="game">{t('The game’s pattern')}</option>
          <option value="hand">{t('Every post typed by hand')}</option>
          {patterns.map((p) => (
            <option key={p.code} value={`rule:${p.code}`}>
              {t('Pattern')}: {p.code}
            </option>
          ))}
          <option value="as">{t('Same result as another draw')}</option>
        </Select>
      </Field>
      {d.resultAs && (
        <>
          <Input value={d.resultAs.product} onChange={(e) => onChange((x) => (x.resultAs!.product = e.target.value.toUpperCase()))} placeholder={t('Product')} className="w-20 font-mono" aria-label={t('Product')} />
          <Input value={d.resultAs.game} onChange={(e) => onChange((x) => (x.resultAs!.game = e.target.value.toUpperCase()))} placeholder={t('Game')} className="w-24 font-mono" aria-label={t('Game')} />
          <Input type="time" value={d.resultAs.time} onChange={(e) => onChange((x) => (x.resultAs!.time = e.target.value))} className="w-28" aria-label={t('Draw time')} />
        </>
      )}
    </div>
  );
}

/** what is not confirmed yet: written down here, shown to the Super Admin in the health checks, until the owner gives the source */
function GapsEditor({ config, change }: { config: ProductConfig; change: (f: (c: ProductConfig) => void) => void }) {
  const t = useT();
  return (
    <Card title={t('Configuration gaps')} className="mb-4">
      <p className="mb-2 text-xs text-ink-500">{t('What is not confirmed yet. Never guess a result rule: write the gap here until the source is given.')}</p>
      <div className="space-y-2">
        {config.gaps.map((g, i) => (
          <div key={i} className="flex flex-wrap items-start gap-2 text-xs">
            <Select value={g.game} onChange={(e) => change((c) => (c.gaps[i]!.game = e.target.value))} className="h-8 text-xs" aria-label={t('Game')}>
              {config.games.map((x) => (
                <option key={x.code}>{x.code}</option>
              ))}
            </Select>
            <Select value={g.time ?? ''} onChange={(e) => change((c) => (e.target.value ? (c.gaps[i]!.time = e.target.value) : delete c.gaps[i]!.time))} className="h-8 text-xs" aria-label={t('Draw')}>
              <option value="">{t('every draw')}</option>
              {(config.games.find((x) => x.code === g.game)?.draws ?? []).map((d) => (
                <option key={d.time}>{d.time}</option>
              ))}
            </Select>
            <textarea value={g.what} onChange={(e) => change((c) => (c.gaps[i]!.what = e.target.value))} rows={2} maxLength={500} className="min-w-64 flex-1 rounded-md border border-ink-200 p-1.5 text-xs" aria-label={t('What is missing')} />
            <Input value={g.source ?? ''} onChange={(e) => change((c) => (e.target.value ? (c.gaps[i]!.source = e.target.value) : delete c.gaps[i]!.source))} placeholder={t('source')} className="h-8 w-36 text-xs" aria-label={t('source')} />
            <button className="mt-2 text-ink-400 hover:text-red-600" onClick={() => change((c) => c.gaps.splice(i, 1))} aria-label={t('Remove')} title={t('Remove')}>
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
      <Button variant="ghost" icon={<Plus size={13} />} onClick={() => change((c) => c.gaps.push({ game: c.games[0]!.code, what: '' }))} disabled={!config.games.length}>
        {t('Add a gap')}
      </Button>
      {config.gaps.length > 0 && (
        <div className="mt-3">
          <ConfigGaps config={config} />
        </div>
      )}
    </Card>
  );
}
